import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { addDoc, collection, doc, serverTimestamp, updateDoc } from "firebase/firestore";
import { dbFs } from "@/lib/firebase";
import { brl, papelLabel, tipoLabel } from "@/lib/format";
import { paraCentavos } from "@/lib/dinheiro";
import {
  PageHeader,
  Panel,
  PanelHeader,
  Th,
  Td,
  EmptyState,
  Campo,
  Escolha,
  Erro,
} from "@/components/parker";
import {
  useEmpresas,
  useContas,
  useContrapartes,
  useCategorias,
  useCentros,
  useProjetos,
  useAlcadas,
  usePerfis,
  useTodosPapeis,
  useIndice,
} from "@/lib/dados";
import { definirPapeis, promoverEmpresa, semear, mensagemDeErro } from "@/lib/fns";
import { cnpjValido, formatarCNPJ } from "@/lib/cnpj";
import { ArrowUpRight } from "lucide-react";
import { useSessao } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PAPEIS, type Papel } from "@/lib/tipos";

export const Route = createFileRoute("/_authenticated/cadastros")({ component: Cadastros });

function Cadastros() {
  const { isAdmin, usuario } = useSessao();

  const empresas = useEmpresas();
  const contas = useContas();
  const contrapartes = useContrapartes();
  const categorias = useCategorias();
  const centros = useCentros();
  const projetos = useProjetos();
  const alcadas = useAlcadas();
  const perfis = usePerfis();
  const papeis = useTodosPapeis();

  const nomeEmpresa = useIndice(empresas.dados);
  const [ocupado, setOcupado] = useState<string | null>(null);
  // Promoção de empresa em constituição (§2).
  const [promovendo, setPromovendo] = useState<string | null>(null);
  const [cnpjNovo, setCnpjNovo] = useState("");
  const [projetoOrigem, setProjetoOrigem] = useState("");

  if (empresas.erro) return <Erro mensagem={empresas.erro} />;

  /** Toda escrita de cadastro carrega quem fez — é o que o gatilho de auditoria lê. */
  const autoria = () => ({
    atualizadoPor: usuario?.uid ?? null,
    atualizadoPorNome: usuario?.displayName ?? usuario?.email ?? null,
    atualizadoEm: serverTimestamp(),
  });

  async function criar(colecao: string, dados: Record<string, unknown>, rotulo: string) {
    setOcupado(colecao);
    try {
      await addDoc(collection(dbFs, colecao), {
        ...dados,
        criadoPor: usuario?.uid ?? null,
        criadoPorNome: usuario?.displayName ?? usuario?.email ?? null,
        createdAt: serverTimestamp(),
        ...autoria(),
      });
      toast.success(`${rotulo} cadastrado(a).`);
    } catch (e) {
      toast.error(mensagemDeErro(e));
    } finally {
      setOcupado(null);
    }
  }

  async function alternar(colecao: string, id: string, campo: string, valor: boolean) {
    try {
      await updateDoc(doc(dbFs, colecao, id), { [campo]: valor, ...autoria() });
    } catch (e) {
      toast.error(mensagemDeErro(e));
    }
  }

  async function trocarPapel(uid: string, papel: Papel, marcado: boolean) {
    const registro = papeis.dados.find((p) => p.uid === uid);
    const atuais = registro?.papeis ?? [];
    const novos = marcado ? [...new Set([...atuais, papel])] : atuais.filter((p) => p !== papel);
    await salvarAcesso(uid, novos, registro?.empresaIds ?? []);
  }

  /** Recorte por empresa (§13). Lista vazia é acesso ao grupo inteiro. */
  async function trocarEmpresa(uid: string, empresaId: string, marcado: boolean) {
    const registro = papeis.dados.find((p) => p.uid === uid);
    const atuais = registro?.empresaIds ?? [];
    const novas = marcado
      ? [...new Set([...atuais, empresaId])]
      : atuais.filter((e) => e !== empresaId);
    await salvarAcesso(uid, registro?.papeis ?? [], novas);
  }

  async function salvarAcesso(uid: string, novosPapeis: Papel[], empresaIds: string[]) {
    setOcupado(uid);
    try {
      await definirPapeis({ uid, papeis: novosPapeis, empresaIds });
      toast.success("Acesso atualizado. A pessoa verá a mudança no próximo carregamento.");
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setOcupado(null);
    }
  }

  async function promover() {
    if (!promovendo) return;
    setOcupado("promover");
    try {
      await promoverEmpresa({
        empresaId: promovendo,
        cnpj: cnpjNovo,
        projetoOrigemId: projetoOrigem || null,
      });
      toast.success("Empresa promovida. O histórico da fase pré-operacional continua onde estava.");
      setPromovendo(null);
      setCnpjNovo("");
      setProjetoOrigem("");
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setOcupado(null);
    }
  }

  async function carregarBase() {
    setOcupado("semear");
    try {
      const r = await semear({});
      toast.success(r.mensagem);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setOcupado(null);
    }
  }

  return (
    <>
      <PageHeader
        title="Cadastros"
        subtitle={
          isAdmin
            ? "Empresas, contas, plano de contas e alçadas. Toda alteração entra na auditoria."
            : "Somente leitura — alterações de cadastro são restritas a administradores."
        }
        actions={
          isAdmin ? (
            <Button
              variant="outline"
              onClick={() => void carregarBase()}
              disabled={ocupado === "semear"}
            >
              Carregar base inicial
            </Button>
          ) : undefined
        }
      />

      <Tabs defaultValue="empresas">
        <TabsList className="mb-4 flex h-auto flex-wrap justify-start">
          <TabsTrigger value="empresas">Empresas</TabsTrigger>
          <TabsTrigger value="contas">Contas</TabsTrigger>
          <TabsTrigger value="contrapartes">Fornecedores e clientes</TabsTrigger>
          <TabsTrigger value="plano">Plano de contas</TabsTrigger>
          <TabsTrigger value="centros">Centros de custo</TabsTrigger>
          <TabsTrigger value="projetos">Projetos</TabsTrigger>
          <TabsTrigger value="alcadas">Alçadas</TabsTrigger>
          <TabsTrigger value="usuarios">Usuários</TabsTrigger>
        </TabsList>

        <TabsContent value="empresas">
          {promovendo ? (
            <Panel className="mb-4 border-emerald/30 p-5">
              <div className="label-caps text-emerald">
                Promover {empresas.dados.find((e) => e.id === promovendo)?.nome}
              </div>
              <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
                Com o CNPJ, a empresa passa a existir para valer e pode receber resultado. Nada do
                passado é reescrito: o que foi gasto na constituição continua reconhecido na
                holding, e o projeto de origem guarda esse histórico como linha própria no painel.
              </p>
              <div className="mt-4 grid gap-4 sm:grid-cols-3">
                <Campo
                  label="CNPJ"
                  hint={
                    cnpjNovo === ""
                      ? "Aceita o formato numérico e o alfanumérico."
                      : cnpjValido(cnpjNovo)
                        ? formatarCNPJ(cnpjNovo)
                        : "Dígitos verificadores não conferem."
                  }
                >
                  <Input
                    className="num"
                    autoFocus
                    value={cnpjNovo}
                    onChange={(e) => setCnpjNovo(e.target.value)}
                    placeholder="00.000.000/0001-00"
                  />
                </Campo>
                <Campo
                  label="Projeto de origem"
                  hint="O projeto que segregou a fase pré-operacional."
                >
                  <Escolha
                    value={projetoOrigem}
                    onChange={setProjetoOrigem}
                    permitirVazio
                    placeholder="Sem projeto"
                    options={projetos.dados
                      .filter((p) => !p.promovidoParaEmpresaId)
                      .map((p) => ({ id: p.id, label: p.nome }))}
                  />
                </Campo>
                <div className="flex items-end gap-2">
                  <Button
                    onClick={() => void promover()}
                    disabled={ocupado === "promover" || !cnpjValido(cnpjNovo)}
                  >
                    Promover
                  </Button>
                  <Button variant="ghost" onClick={() => setPromovendo(null)}>
                    Cancelar
                  </Button>
                </div>
              </div>
            </Panel>
          ) : null}
          <Formulario
            visivel={isAdmin}
            campos={[
              { chave: "nome", label: "Nome" },
              { chave: "cnpj", label: "CNPJ" },
              {
                chave: "tipo",
                label: "Tipo",
                opcoes: [
                  { id: "holding", label: "Holding" },
                  { id: "operacional", label: "Operacional" },
                  { id: "pre_operacional", label: "Pré-operacional" },
                ],
                padrao: "operacional",
              },
            ]}
            ocupado={ocupado === "empresas"}
            onSalvar={(v) =>
              criar(
                "empresas",
                {
                  nome: v.nome,
                  cnpj: v.cnpj || null,
                  tipo: v.tipo,
                  holdingMaeId: null,
                  ativa: true,
                },
                "Empresa",
              )
            }
          />
          <Lista
            colunas={["Nome", "CNPJ", "Tipo", "Ativa", ""]}
            vazio="Nenhuma empresa"
            linhas={empresas.dados.map((e) => [
              <span key={`n-${e.id}`}>
                {e.nome}
                {e.projetoOrigemId ? (
                  <span className="label-caps block">promovida de projeto</span>
                ) : null}
              </span>,
              e.cnpj ? formatarCNPJ(e.cnpj) : "—",
              e.tipo === "pre_operacional" ? "em constituição" : e.tipo,
              <Switch
                key={`a-${e.id}`}
                checked={e.ativa}
                disabled={!isAdmin}
                onCheckedChange={(v) => void alternar("empresas", e.id, "ativa", v)}
              />,
              isAdmin && e.tipo === "pre_operacional" ? (
                <Button
                  key={`p-${e.id}`}
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    setPromovendo(e.id);
                    setCnpjNovo("");
                    setProjetoOrigem(e.projetoOrigemId ?? "");
                  }}
                >
                  <ArrowUpRight className="size-3.5" /> Promover
                </Button>
              ) : null,
            ])}
          />
        </TabsContent>

        <TabsContent value="contas">
          <Formulario
            visivel={isAdmin}
            campos={[
              {
                chave: "empresaId",
                label: "Empresa",
                opcoes: empresas.dados.map((e) => ({ id: e.id, label: e.nome })),
              },
              { chave: "nome", label: "Nome da conta" },
              { chave: "banco", label: "Banco" },
              { chave: "saldo", label: "Saldo inicial (R$)", numerico: true },
            ]}
            ocupado={ocupado === "contasBancarias"}
            onSalvar={(v) =>
              criar(
                "contasBancarias",
                {
                  empresaId: v.empresaId,
                  nome: v.nome,
                  banco: v.banco || null,
                  saldoInicialCentavos: paraCentavos(v.saldo),
                  ativa: true,
                },
                "Conta",
              )
            }
          />
          <Lista
            colunas={["Conta", "Banco", "Empresa", "Saldo inicial", "Ativa"]}
            vazio="Nenhuma conta"
            linhas={contas.dados.map((c) => [
              c.nome,
              c.banco ?? "—",
              nomeEmpresa.get(c.empresaId) ?? "—",
              brl(c.saldoInicialCentavos),
              <Switch
                key={c.id}
                checked={c.ativa}
                disabled={!isAdmin}
                onCheckedChange={(v) => void alternar("contasBancarias", c.id, "ativa", v)}
              />,
            ])}
          />
        </TabsContent>

        <TabsContent value="contrapartes">
          <Formulario
            visivel={isAdmin}
            campos={[
              { chave: "nome", label: "Nome" },
              { chave: "documento", label: "CNPJ / CPF" },
              {
                chave: "tipo",
                label: "Tipo",
                opcoes: [
                  { id: "fornecedor", label: "Fornecedor" },
                  { id: "cliente", label: "Cliente" },
                  { id: "ambos", label: "Ambos" },
                ],
                padrao: "fornecedor",
              },
            ]}
            ocupado={ocupado === "contrapartes"}
            onSalvar={(v) =>
              criar(
                "contrapartes",
                { nome: v.nome, documento: v.documento || null, tipo: v.tipo },
                "Contraparte",
              )
            }
          />
          <Lista
            colunas={["Nome", "Documento", "Tipo"]}
            vazio="Nenhum fornecedor ou cliente"
            linhas={contrapartes.dados.map((c) => [c.nome, c.documento ?? "—", c.tipo])}
          />
        </TabsContent>

        <TabsContent value="plano">
          <Formulario
            visivel={isAdmin}
            campos={[
              { chave: "codigo", label: "Código" },
              { chave: "nome", label: "Nome" },
              {
                chave: "tipo",
                label: "Tipo",
                opcoes: [
                  { id: "receita", label: "Receita" },
                  { id: "despesa", label: "Despesa" },
                  { id: "nao_operacional", label: "Não operacional" },
                ],
                padrao: "despesa",
              },
              { chave: "grupo", label: "Grupo" },
            ]}
            ocupado={ocupado === "categorias"}
            onSalvar={(v) =>
              criar(
                "categorias",
                { codigo: v.codigo, nome: v.nome, tipo: v.tipo, grupo: v.grupo || "", ativa: true },
                "Categoria",
              )
            }
          />
          <Lista
            colunas={["Código", "Nome", "Tipo", "Grupo", "Ativa"]}
            vazio="Plano de contas vazio"
            linhas={categorias.dados.map((c) => [
              c.codigo,
              c.nome,
              tipoLabel[c.tipo] ?? c.tipo,
              c.grupo,
              <Switch
                key={c.id}
                checked={c.ativa}
                disabled={!isAdmin}
                onCheckedChange={(v) => void alternar("categorias", c.id, "ativa", v)}
              />,
            ])}
          />
        </TabsContent>

        <TabsContent value="centros">
          <Formulario
            visivel={isAdmin}
            campos={[
              { chave: "codigo", label: "Código" },
              { chave: "nome", label: "Nome" },
            ]}
            ocupado={ocupado === "centrosCusto"}
            onSalvar={(v) =>
              criar(
                "centrosCusto",
                { codigo: v.codigo, nome: v.nome, ativo: true },
                "Centro de custo",
              )
            }
          />
          <Lista
            colunas={["Código", "Nome", "Ativo"]}
            vazio="Nenhum centro de custo"
            linhas={centros.dados.map((c) => [
              c.codigo,
              c.nome,
              <Switch
                key={c.id}
                checked={c.ativo}
                disabled={!isAdmin}
                onCheckedChange={(v) => void alternar("centrosCusto", c.id, "ativo", v)}
              />,
            ])}
          />
        </TabsContent>

        <TabsContent value="projetos">
          <Formulario
            visivel={isAdmin}
            campos={[
              { chave: "nome", label: "Nome do projeto" },
              {
                chave: "empresaId",
                label: "Empresa",
                opcoes: empresas.dados.map((e) => ({ id: e.id, label: e.nome })),
              },
            ]}
            ocupado={ocupado === "projetos"}
            onSalvar={(v) =>
              criar(
                "projetos",
                { nome: v.nome, empresaId: v.empresaId || null, ativo: true },
                "Projeto",
              )
            }
          />
          <Lista
            colunas={["Projeto", "Empresa", "Ativo"]}
            vazio="Nenhum projeto"
            linhas={projetos.dados.map((p) => [
              p.nome,
              p.empresaId ? (nomeEmpresa.get(p.empresaId) ?? "—") : "—",
              <Switch
                key={p.id}
                checked={p.ativo}
                disabled={!isAdmin}
                onCheckedChange={(v) => void alternar("projetos", p.id, "ativo", v)}
              />,
            ])}
          />
        </TabsContent>

        <TabsContent value="alcadas">
          <Panel className="mb-4 p-4 text-sm text-muted-foreground">
            A alçada define quantas aprovações cada faixa de valor exige — e é aplicada no servidor,
            no momento em que o lançamento é criado. Alterar uma faixa muda o controle de tudo que
            vier depois.
          </Panel>
          <Formulario
            visivel={isAdmin}
            campos={[
              { chave: "min", label: "Valor mínimo (R$)", numerico: true },
              { chave: "max", label: "Valor máximo (R$)", numerico: true },
              { chave: "n", label: "Aprovadores", numerico: true, padrao: "1" },
            ]}
            ocupado={ocupado === "alcadas"}
            onSalvar={(v) =>
              criar(
                "alcadas",
                {
                  valorMinCentavos: paraCentavos(v.min),
                  valorMaxCentavos: v.max ? paraCentavos(v.max) : null,
                  aprovadoresNecessarios: Math.max(1, Number(v.n) || 1),
                  solicitanteConta: false,
                },
                "Faixa de alçada",
              )
            }
          />
          <Lista
            colunas={["De", "Até", "Aprovadores", "Solicitante conta"]}
            vazio="Nenhuma faixa definida — tudo exige uma aprovação."
            linhas={alcadas.dados.map((a) => [
              brl(a.valorMinCentavos),
              a.valorMaxCentavos === null ? "acima" : brl(a.valorMaxCentavos),
              String(a.aprovadoresNecessarios),
              <Switch
                key={a.id}
                checked={a.solicitanteConta}
                disabled={!isAdmin}
                onCheckedChange={(v) => void alternar("alcadas", a.id, "solicitanteConta", v)}
              />,
            ])}
          />
        </TabsContent>

        <TabsContent value="usuarios">
          <Panel className="mb-4 p-4 text-sm text-muted-foreground">
            Quem entra com a conta do Workspace aparece aqui automaticamente, como lançador. O
            primeiro acesso do grupo recebe administrador. Lançador registra, aprovador assina,
            leitor só acompanha.
          </Panel>
          <Panel className="overflow-x-auto">
            <PanelHeader title="Pessoas com acesso" />
            <table className="w-full min-w-[720px] text-sm">
              <thead>
                <tr>
                  <Th>Nome</Th>
                  <Th>E-mail</Th>
                  {PAPEIS.map((p) => (
                    <Th key={p} className="text-center">
                      {papelLabel[p]}
                    </Th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {perfis.dados.map((perfil) => {
                  const meus = papeis.dados.find((p) => p.uid === perfil.id)?.papeis ?? [];
                  return (
                    <tr key={perfil.id}>
                      <Td>{perfil.nome}</Td>
                      <Td className="text-muted-foreground">{perfil.email}</Td>
                      {PAPEIS.map((p) => (
                        <Td key={p} className="text-center">
                          <Switch
                            checked={meus.includes(p)}
                            disabled={!isAdmin || ocupado === perfil.id}
                            onCheckedChange={(v) => void trocarPapel(perfil.id, p, v)}
                          />
                        </Td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {perfis.dados.length === 0 ? <EmptyState title="Nenhum usuário ainda" /> : null}
          </Panel>

          <Panel className="mt-4 overflow-x-auto">
            <PanelHeader
              title="Acesso por empresa"
              extra={<span className="label-caps">nenhuma marcada = grupo inteiro</span>}
            />
            <p className="px-4 pt-3 text-xs text-muted-foreground">
              Limita em quais empresas a pessoa pode lançar, aprovar e ratear. A visão gerencial
              continua compartilhada: o recorte vale para o que ela faz, não para o que ela lê.
              Administrador não aceita recorte.
            </p>
            <table className="mt-2 w-full min-w-[640px] text-sm">
              <thead>
                <tr>
                  <Th>Pessoa</Th>
                  {empresas.dados.map((e) => (
                    <Th key={e.id} className="text-center">
                      {e.nome}
                    </Th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {perfis.dados.map((perfil) => {
                  const registro = papeis.dados.find((p) => p.uid === perfil.id);
                  const ehAdmin = (registro?.papeis ?? []).includes("admin");
                  const minhas = registro?.empresaIds ?? [];
                  return (
                    <tr key={perfil.id}>
                      <Td>
                        {perfil.nome}
                        <div className="label-caps">
                          {ehAdmin
                            ? "administrador · grupo inteiro"
                            : minhas.length === 0
                              ? "grupo inteiro"
                              : `${minhas.length} empresa(s)`}
                        </div>
                      </Td>
                      {empresas.dados.map((e) => (
                        <Td key={e.id} className="text-center">
                          <Switch
                            checked={minhas.includes(e.id)}
                            disabled={!isAdmin || ehAdmin || ocupado === perfil.id}
                            onCheckedChange={(v) => void trocarEmpresa(perfil.id, e.id, v)}
                          />
                        </Td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Panel>
        </TabsContent>
      </Tabs>
    </>
  );
}

// ---------- blocos reaproveitados pelas abas ----------

interface CampoForm {
  chave: string;
  label: string;
  opcoes?: Array<{ id: string; label: string }>;
  numerico?: boolean;
  padrao?: string;
}

function Formulario({
  campos,
  onSalvar,
  ocupado,
  visivel,
}: {
  campos: CampoForm[];
  onSalvar: (valores: Record<string, string>) => void | Promise<void>;
  ocupado: boolean;
  visivel: boolean;
}) {
  const inicial = Object.fromEntries(campos.map((c) => [c.chave, c.padrao ?? ""]));
  const [v, setV] = useState<Record<string, string>>(inicial);

  if (!visivel) return null;

  return (
    <Panel className="mb-4 grid gap-4 p-5 sm:grid-cols-4">
      {campos.map((c) => (
        <Campo key={c.chave} label={c.label}>
          {c.opcoes ? (
            <Escolha
              value={v[c.chave] ?? ""}
              onChange={(nv) => setV((s) => ({ ...s, [c.chave]: nv }))}
              options={c.opcoes}
            />
          ) : (
            <Input
              className={c.numerico ? "num" : undefined}
              inputMode={c.numerico ? "decimal" : undefined}
              value={v[c.chave] ?? ""}
              onChange={(e) => setV((s) => ({ ...s, [c.chave]: e.target.value }))}
            />
          )}
        </Campo>
      ))}
      <div className="flex items-end">
        <Button
          disabled={ocupado}
          onClick={async () => {
            await onSalvar(v);
            setV(inicial);
          }}
        >
          Adicionar
        </Button>
      </div>
    </Panel>
  );
}

function Lista({
  colunas,
  linhas,
  vazio,
}: {
  colunas: string[];
  linhas: React.ReactNode[][];
  vazio: string;
}) {
  return (
    <Panel className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr>
            {colunas.map((c) => (
              <Th key={c}>{c}</Th>
            ))}
          </tr>
        </thead>
        <tbody>
          {linhas.map((linha, i) => (
            <tr key={i}>
              {linha.map((celula, j) => (
                <Td key={j} className={j === 0 ? "font-medium" : "text-muted-foreground"}>
                  {celula}
                </Td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {linhas.length === 0 ? <EmptyState title={vazio} /> : null}
    </Panel>
  );
}
